import {
  SRewardItem,
  cancelWelcomeBonus,
  claimDailyReward,
} from '@/apis/rewards';
import queryKeysFactory from '@/reactQueryKeys/keysFactory';
import useTheme from '@/theme/useTheme';
import { formatMoneyByCurrency } from '@/utils';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { Trans, useTranslation } from 'next-i18next';
import { TickCircleWrapper } from './helper';
import WrappedButton from '@/components/ui/WrappedButton';
import { TInferSchemaType } from '@/constants/schemas';
import convertToOrdinal from '@/utils/number/toOrdinalNumbers';
import { Check, ChevronRight, Close, Lock } from '@mui/icons-material';
import { useCountDown, useRequest } from 'ahooks';
import Link from 'next/link';
import React, { PropsWithChildren } from 'react';
import queryClient from '@/reactQueryKeys/queryClient';
import { GLOBAL_MODALS, globalModals } from '@/state/globalSettings';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import DepositBonusTAndCModal from './depositBonusTAndCModal';
import InfoTooltip from '@/components/ui/common/InfoTooltip';
import formatNumber from '@/utils/number/formatter';
import { noop } from 'lodash-es';
import useWalletModal from '@/hooks/biz/useWalletModal';
import MoneyWithCurrencyIcon from '../MoneyWithCurrencyIcon';
import CircleProgress from './circleProgress';
import BonusCountDown from './bonusCountDown';
import { ActivityStatusEnum } from '@/apis/promotions';
import { confirm } from 'material-ui-confirm';
import { depositBonus } from '@/reactQueryKeys/modules/rewards';

const DEPOSIT_BONUS_PERCENT_CONFIG = [1.25, 1.5, 1.75, 2];

const DepositBonusTabWrapper: React.FC<
  PropsWithChildren<{
    currentIndex?: number;
    depositBonus: TInferSchemaType<typeof SRewardItem>;
  }>
> = ({ currentIndex = -1, children, depositBonus }) => {
  const [t] = useTranslation();
  const { theme } = useTheme();

  return (
    <Stack
      sx={{
        background: theme.palette.bg.secondary,
        padding: theme.spacing(3),
        borderRadius: theme.spacing(1),
        borderTopLeftRadius: 0,
        // mb: 2,
        gap: 2,
        position: 'relative',
        flex: 1,
        justifyContent: 'center',
      }}
    >
      <Box>{children}</Box>
    </Stack>
  );
};

const ActiveDepositeBonusDetails: React.FC<{
  depositBonusDetail: TInferSchemaType<typeof SRewardItem>;
  handleClose?: () => void;
}> = ({ depositBonusDetail, handleClose = noop }) => {
  const [t] = useTranslation();

  const {
    currentInProgressIndex,
    bonusAmount,
    availableAmount,
    betTotal,
    contribute,
    withdrawAbleTotal,
    withdrawTotal,
    depositAmount,
    expireTime,
    betExpireTime,
    multiplier,
    grandSum,
    totalPossibleCompletionCount,
    completedCount,
    minDeposit,
    maxBonus,
  } = depositBonusDetail;

  const { theme } = useTheme();

  const { push } = useRouter();

  const openWalletModal = useWalletModal();

  const [TCOpen, setTCOpen] = React.useState(false);

  const { run: claimReward, loading } = useRequest(
    (bonus: string) => {
      return claimDailyReward(bonus);
    },
    {
      manual: true,
      onSuccess: (data) => {
        queryClient.invalidateQueries(queryKeysFactory.rewards.activityQueue());

        toast.success(t('Claim Success!'));
      },
    },
  );

  const { runAsync, loading: loadingCancel } = useRequest(
    () => cancelWelcomeBonus(),
    {
      manual: true,
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeysFactory.rewards.activityQueue());
        toast.success(t('Cancel Success'));
      },
    },
  );

  const isAllCompleted =
    completedCount >= totalPossibleCompletionCount &&
    currentInProgressIndex === -1;

  const hasNottStarted = currentInProgressIndex === -1 || !depositBonusDetail;

  if (isAllCompleted) {
    return (
      <DepositBonusTabWrapper depositBonus={depositBonusDetail}>
        <Typography variant="h3">{t('All Deposit bonus completed')}</Typography>
      </DepositBonusTabWrapper>
    );
  }

  if (hasNottStarted) {
    return (
      <DepositBonusTabWrapper depositBonus={depositBonusDetail}>
        <WrappedButton
          fullWidth
          onClick={() => {
            openWalletModal();
          }}
        >
          {t('Deposit Now')}
        </WrappedButton>

        <Typography mt={1}>
          Min Bonus: <MoneyWithCurrencyIcon value={minDeposit} /> Max Bonus:{' '}
          <MoneyWithCurrencyIcon value={maxBonus} />
        </Typography>
      </DepositBonusTabWrapper>
    );
  }

  const ongoingIndex = Math.floor(
    Number(withdrawAbleTotal) / (Number(bonusAmount) / 10),
  );
  const claimedIndex =
    Math.floor(Number(withdrawTotal) / (Number(bonusAmount) / 10)) - 1;

  const totalAvailableAmount =
    (Number(grandSum?.availableFromPreviousDepositBonus) || 0) +
    (availableAmount || 0);

  const hasOnGoingDepositBonus = currentInProgressIndex >= 0;
  // const hasOnGoingDepositBonus = true;

  const countdown = hasOnGoingDepositBonus ? (
    <BonusCountDown countdown={betExpireTime} />
  ) : (
    <Typography color={'text.secondary'} alignSelf={'center'}>
      {t('Completed')}
    </Typography>
  );

  const wagerRequired = Number(depositAmount) * 60;
  const currentWagered = Number(betTotal);

  return (
    <>
      {/* <Typography
        padding={1}
        sx={{
          background: theme.palette.primary.main,
          color: theme.palette.grey['1000'],
          borderTopRightRadius: theme.spacing(1),
          fontWeight: 500,
          display: 'inline-block',
        }}
      >
        {t('rewards::Welcome Bonus{{percent}}', {
          percent: hasOnGoingDepositBonus
            ? ` (${Number(multiplier) * 100}%)`
            : '',
        })}
      </Typography> */}
      <DepositBonusTabWrapper depositBonus={depositBonusDetail}>
        <Stack justifyContent={'space-between'} direction={'row'}>
          <Typography
            variant="caption"
            display={'flex'}
            alignItems={'center'}
            color={'primary.main'}
          >
            <Link href={'/benefits/deposit'}>
              {t('rewards::Bonus Details')}{' '}
            </Link>
            <ChevronRight fontSize="small"></ChevronRight>
          </Typography>
          <WrappedButton
            size="small"
            variant="text"
            endIcon={<Close></Close>}
            onClick={() => {
              // cancelActivity();
              confirm({
                title: t('Cancel Bonus'),
                description: (
                  <Trans
                    i18nKey={'cancel_bonus'}
                    defaults={
                      'You have selected to cancel this bonus. Cancelling this bonus will remove the bonus entirely. You will not get it back or reactivate the current bonus. <br></br> You have <money></money> unclaimed bonus. This will be transferred once you confirmed the cancellation. Are you sure you want to cancel?'
                    }
                    components={{
                      br: <br />,
                      money: (
                        <MoneyWithCurrencyIcon value={availableAmount ?? 0} />
                      ),
                    }}
                  ></Trans>
                ),
                confirmationText: t('Yes'),
                cancellationText: t('No'),
              })
                .then(() => {
                  runAsync();
                })
                .catch(() => {
                  // cancel
                });
              // toast.error(t('Please claim your bonus first'));
            }}
            isLoading={loadingCancel}
          >
            {t('Cancel')}
          </WrappedButton>
        </Stack>
        <Stack
          direction={['column', 'row']}
          gap={2}
          justifyContent={'space-between'}
        >
          <Stack gap={2}>
            {Number(grandSum?.availableFromPreviousDepositBonus) &&
            hasOnGoingDepositBonus ? (
              <Typography fontWeight={600} color={'text.secondary'}>
                <b style={{ display: 'inline-flex', justifyContent: 'center' }}>
                  {t('rewards::Previous Unclaimed Bonus')}:&nbsp;
                  {formatMoneyByCurrency(
                    Number(grandSum?.availableFromPreviousDepositBonus),
                    {
                      withCurrencySymbol: true,
                    },
                  )}
                </b>
              </Typography>
            ) : null}
            <Stack gap={0.5}>
              <Stack
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
              >
                <Typography
                  color={'text.secondary'}
                  display={hasOnGoingDepositBonus ? undefined : 'none'}
                >
                  {t('Deposit')}:&nbsp;
                  <MoneyWithCurrencyIcon
                    value={depositAmount}
                    withCurrencySymbol
                  />
                </Typography>
              </Stack>
              <Typography display={hasOnGoingDepositBonus ? undefined : 'none'}>
                {t('Bonus')}:{' '}
                <MoneyWithCurrencyIcon value={bonusAmount} withCurrencySymbol />
              </Typography>
              <Typography display={hasOnGoingDepositBonus ? undefined : 'none'}>
                <span
                  style={{ display: 'inline-flex', justifyContent: 'center' }}
                >
                  {t('rewards::Wager Contributed')}
                  <InfoTooltip
                    text={
                      <Trans
                        i18nKey="Wager contributed during this Welcome Bonus. Game Category Wagering Contribution % can be found in Bonus Details."
                        components={{
                          br: <br />,
                        }}
                      />
                    }
                  />
                  :&nbsp;
                  <MoneyWithCurrencyIcon value={betTotal} withCurrencySymbol />(
                  {formatNumber(contribute * 100, {
                    precision: 2,
                  })}
                  %)
                </span>
              </Typography>
            </Stack>
            <Typography
              display={hasOnGoingDepositBonus ? undefined : 'none'}
              fontSize={12}
            >
              {t(
                'Wager 6x the deposit amount you made to active the deposit bonus to unlock 10% of the bonus!',
              )}
            </Typography>
            <Stack display={'grid'} gap={1} gridTemplateColumns={'1fr auto'}>
              <WrappedButton
                sx={{ height: '100%', fontSize: '18px' }}
                onClick={() => {
                  if (totalAvailableAmount) {
                    claimReward('DEPOSITBONUS');
                  } else {
                    handleClose();
                    push('/casino');
                  }
                }}
                isLoading={loading}
              >
                {totalAvailableAmount ? (
                  <>
                    {t('rewards::Claim Available')}
                    &nbsp;
                    {totalAvailableAmount ? (
                      <MoneyWithCurrencyIcon value={totalAvailableAmount} />
                    ) : null}
                  </>
                ) : (
                  t('rewards::Play Games')
                )}
              </WrappedButton>
              {countdown}
            </Stack>
          </Stack>
          <Stack
            gap={1}
            sx={{
              background: theme.palette.bg.secondary,
              flex: 1,
            }}
            borderRadius={theme.spacing(2)}
          >
            <Stack direction={'row'} justifyContent={'space-between'}>
              <Typography color={'text.secondary'}>
                {t('rewards::You Claimed:')}
              </Typography>
              <MoneyWithCurrencyIcon value={depositBonusDetail.claimedAmount} />
            </Stack>

            {ongoingIndex !== 9 && (
              <Stack direction={'row'} justifyContent={'space-between'}>
                <Typography color={'text.secondary'}>
                  {t('rewards::Available Bonus:')}
                </Typography>
                &nbsp;
                <MoneyWithCurrencyIcon value={availableAmount} />
              </Stack>
            )}
            <Stack
              display={hasOnGoingDepositBonus ? 'grid' : 'none'}
              gridTemplateColumns={'repeat(5, auto)'}
              gridTemplateRows={'2'}
              justifyContent={'space-between'}
              gap={1}
              mt={2}
            >
              <CircleProgress
                wagerRequired={wagerRequired}
                currentWagered={currentWagered}
                // ongoingIndex={ongoingIndex}
                claimedIndex={claimedIndex}
              />
            </Stack>
          </Stack>
        </Stack>
      </DepositBonusTabWrapper>
      <DepositBonusTAndCModal open={TCOpen} setOpen={setTCOpen} />
    </>
  );
};

export default ActiveDepositeBonusDetails;
